import React, { useState, useCallback, useRef, useEffect } from "react";
import { Refresh } from "@mui/icons-material";
import { TableModel } from "./types";
import { ColumnChooser } from "./ColumnChooser";
import "./TableToolbar.scss";

export interface ToolbarAction {
  label: string;
  onClick: () => void;
  /** a dropdown: the button opens these instead of calling onClick */
  children?: Array<{ label: string; description?: string; onClick: () => void }>;
  disabled?: boolean;
  variant?: "primary" | "secondary" | "danger";
  // icon may be a string or a React node (SVG/JSX)
  icon?: React.ReactNode;
  // optional placement: left (default) or right (far end of toolbar)
  align?: "left" | "right";
}

interface TableToolbarProps<T = unknown> {
  model: TableModel<T>;
  enableSearch?: boolean;
  enableColumnChooser?: boolean;
  showSaveButtons?: boolean;
  onModelChange?: () => void;
  onSaveClick?: () => void;
  onDiscardClick?: () => void;
  /** the Discard button was clicked once and is waiting for the confirming click */
  discardArmed?: boolean;
  actions?: ToolbarAction[];
  /** rows shown / rows loaded, for the count at the far right */
  rowCount?: number;
  totalCount?: number;
  /**
   * Re-run the view's query. Every view that can be re-queried gets the
   * control from the renderer, so a page never declares its own Refresh
   * action (glen, 2026-09-10, FEATURE-20260910-page-dsl-view-polish).
   */
  onRefresh?: () => void;
  /** a query is in flight — the control spins and does not stack a second one */
  refreshing?: boolean;
  /** a status line after the search box (a failed refresh while rows are shown) */
  statusNode?: React.ReactNode;
}

export function TableToolbar<T = unknown>({
  model,
  enableSearch = true,
  enableColumnChooser = true,
  showSaveButtons = false,
  onModelChange,
  onSaveClick,
  onDiscardClick,
  discardArmed,
  actions = [],
  rowCount,
  totalCount,
  onRefresh,
  refreshing = false,
  statusNode,
}: TableToolbarProps<T>) {
  const [searchValue, setSearchValue] = useState(model.searchText || "");
  // Which dropdown action is open (by index in leftActions), if any.
  const [openMenu, setOpenMenu] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (openMenu === null) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpenMenu(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [openMenu]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSearchValue(value);
      model.setSearchText(value);
      onModelChange?.();
    },
    [model, onModelChange]
  );

  const handleClearSearch = useCallback(() => {
    setSearchValue("");
    model.setSearchText("");
    onModelChange?.();
  }, [model, onModelChange]);

  const handleColumnVisibilityChange = useCallback(
    (columnId: string, visible: boolean) => {
      model.updateColumn(columnId, { visible });
      onModelChange?.();
    },
    [model, onModelChange]
  );

  const hasChanges = model.hasUnsavedChanges();
  const changeCounts = model.changeTracker?.getChangeCounts();

  const getButtonClass = (variant?: string) => {
    switch (variant) {
      case "primary":
        return "action-button-primary";
      case "danger":
        return "action-button-danger";
      case "secondary":
      default:
        return "action-button-secondary";
    }
  };

  // split actions into left/right groups so callers can pin actions to the far right
  const leftActions = actions.filter((a) => a.align !== "right");
  const rightActions = actions.filter((a) => a.align === "right");

  return (
    <div className="table-toolbar">
      {enableSearch && (
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Search across all columns..."
            value={searchValue}
            onChange={handleSearchChange}
          />
          {searchValue && (
            <button
              className="clear-search-button"
              onClick={handleClearSearch}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      )}

      {statusNode && (
        <div className="toolbar-status" style={{ flex: 1, minWidth: 0, overflow: "hidden", display: "flex", alignItems: "center", fontSize: "0.85rem" }}>
          {statusNode}
        </div>
      )}

      {(leftActions.length > 0 || enableColumnChooser || onRefresh) && (
        <div className="toolbar-actions">
          {leftActions.map((action, index) =>
            action.children ? (
              <div
                key={index}
                className="toolbar-menu"
                ref={openMenu === index ? menuRef : undefined}
              >
                <button
                  className={getButtonClass(action.variant)}
                  onClick={() => setOpenMenu(openMenu === index ? null : index)}
                  disabled={action.disabled}
                  title={action.label}
                >
                  {action.icon && <span className="button-icon">{action.icon}</span>}
                  {action.label}
                  <span className="toolbar-menu-caret">▾</span>
                </button>
                {openMenu === index && (
                  <div className="toolbar-menu-dropdown">
                    {action.children.map((child, ci) => (
                      <button
                        key={ci}
                        className="toolbar-menu-item"
                        onClick={() => {
                          setOpenMenu(null);
                          child.onClick();
                        }}
                      >
                        <span className="toolbar-menu-item-label">{child.label}</span>
                        {child.description && (
                          <span className="toolbar-menu-item-desc">{child.description}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <button
                key={index}
                className={getButtonClass(action.variant)}
                onClick={action.onClick}
                disabled={action.disabled}
                title={action.label}
              >
                {action.icon && (
                  <span className="button-icon">{action.icon}</span>
                )}
                {action.label}
              </button>
            ),
          )}

          {onRefresh && (
            <button
              className={`toolbar-refresh-button${refreshing ? " refreshing" : ""}`}
              onClick={onRefresh}
              disabled={refreshing}
              title="Refresh"
              aria-label="Refresh"
            >
              <span className="refresh-icon">
                <Refresh />
              </span>
            </button>
          )}

          {enableColumnChooser && (
            <ColumnChooser
              columns={model.columns}
              onColumnVisibilityChange={handleColumnVisibilityChange}
            />
          )}
        </div>
      )}

      {rowCount !== undefined && (
        <span className="toolbar-count" title="rows shown / rows loaded">
          {totalCount !== undefined && totalCount !== rowCount
            ? `${rowCount} of ${totalCount} rows`
            : `${rowCount} row${rowCount === 1 ? "" : "s"}`}
        </span>
      )}
      {rightActions.length > 0 && (
        <div className="toolbar-actions-right">
          {rightActions.map((action, index) => (
            <button
              key={index}
              className={getButtonClass(action.variant)}
              onClick={action.onClick}
              disabled={action.disabled}
              title={action.label}
            >
              {action.icon && (
                <span className="button-icon">{action.icon}</span>
              )}
              {action.label}
            </button>
          ))}
        </div>
      )}

      {showSaveButtons && hasChanges && (
        <div className="save-buttons-container">
          <span className="changes-badge">
            {changeCounts?.total} change{changeCounts?.total !== 1 ? "s" : ""}
          </span>
          <button
            className={`discard-button${discardArmed ? " armed" : ""}`}
            onClick={onDiscardClick}
            title={discardArmed ? "Click again to throw away every staged change" : "Discard all changes"}
          >
            {discardArmed
              ? `Click again to discard ${changeCounts?.total ?? ""} change${changeCounts?.total !== 1 ? "s" : ""}`
              : "Discard"}
          </button>
          <button
            className="save-button"
            onClick={onSaveClick}
            title="Save all changes"
          >
            Save Changes
          </button>
        </div>
      )}
    </div>
  );
}
